import {
    Dialog,
    Typography,
    TextField,
    styled,
    Button,
    Grid,
    Alert,
    Box,
    CircularProgress,
} from '@mui/material';
import {
    useState, useEffect, MouseEvent,
} from 'react';
import { Cancel, FileCopy } from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { useMutation } from '@tanstack/react-query';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useDebouncedProductLookup } from 'src/hooks/useDebouncedProductLookup';
import { SetsConfigService } from 'src/services/SetsConfigService';
import { SaveLoadingButton } from 'src/components/common/buttons/SaveLoadingButton';
import { CSSGrid } from 'src/components/common/CSSGrid';

type SetsConfigCultureGroup = Models.IngestConfigApi.SetsConfigs.SetsConfigCultureGroup;

export interface PropTypes {
    open: boolean;
    currentProductKey: string;
    onClose: () => void;
    onCopy: (groups: SetsConfigCultureGroup[]) => void;
}

const StyledDialog = styled(Dialog)(() => ({
    '& .MuiPaper-root': {
        width: '100%',
        maxWidth: '600px',
    },
    '& .MuiDialog-container': {
        width: '100%',
    },
}));

const DialogContainer = styled(CSSGrid)(({ theme }) => ({
    gap: theme.spacing(4),
    overflow: 'hidden',
    padding: theme.spacing(8),
}));

const LookupStatus = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minHeight: theme.spacing(6),
}));

const ActionButtons = styled(CSSGrid)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(4),
    justifyContent: 'flex-end',
    gridAutoFlow: 'column dense',
    marginTop: theme.spacing(4),
}));

export const CopyFromProductModal = ({
    open,
    currentProductKey,
    onClose,
    onCopy,
}: PropTypes): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);

    const [error, setError] = useState<string | null>(null);
    const {
        productKey,
        resolvedName,
        notFound,
        isLookingUp,
        setProductKey,
        reset,
    } = useDebouncedProductLookup(accessToken);

    useEffect(() => {
        if (open) {
            reset();
            setError(null);
        }
    }, [open, reset]);
    const isSameProduct = !!productKey.trim()
        && productKey.trim().toUpperCase() === currentProductKey.toUpperCase();

    const productKeyHelperText = isSameProduct
        ? 'Pick a different product to copy from'
        : 'Product key, e.g. PRD-XXXXXXXX';

    const loadSourceConfigMutation = useMutation<
    Models.IngestConfigApi.SetsConfigs.ProductSetsConfig | null,
    Error,
    string
    >(
        (sourceProductKey) => SetsConfigService.getSetsConfig(accessToken, sourceProductKey),
    );

    const isCopying = loadSourceConfigMutation.isLoading;

    const handleProductKeyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setProductKey(event.target.value.toUpperCase());
        setError(null);
    };

    const handleCopy = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault();

        if (!productKey.trim() || isSameProduct) {
            return;
        }

        try {
            const sourceConfig = await loadSourceConfigMutation.mutateAsync(productKey.trim());

            if (!sourceConfig) {
                setError('That product has no sets config to copy.');
                return;
            }

            onCopy(sourceConfig.cultureGroups);
            onClose();
        } catch (err) {
            setError((err as Error).message);
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose}>
            <DialogContainer>
                <Typography variant="h6">
                    Copy From Another Product
                </Typography>
                <Typography variant="caption">
                    Enter a product key to copy its culture groups and sets into this config. The
                    copy becomes an unsaved edit here, and the source config is left untouched.
                </Typography>
                <Grid container gap={4}>
                    <TextField
                        fullWidth
                        required
                        error={isSameProduct}
                        helperText={productKeyHelperText}
                        id="copy-from-product-key"
                        label="Product (PRD)"
                        size="small"
                        value={productKey}
                        onChange={handleProductKeyChange}
                    />
                </Grid>
                <LookupStatus>
                    {isLookingUp && (
                        <>
                            <CircularProgress size={16} />
                            <Typography color="text.secondary" variant="body2">
                                Looking up product...
                            </Typography>
                        </>
                    )}
                    {resolvedName && (
                        <Typography variant="body2">
                            Product:
                            {' '}
                            <strong>{resolvedName}</strong>
                        </Typography>
                    )}
                    {notFound && (
                        <Typography color="text.secondary" variant="body2">
                            No product name found for this key. Copy still works if it has a config.
                        </Typography>
                    )}
                </LookupStatus>
                {error && <Alert severity="error">{error}</Alert>}
                <ActionButtons>
                    <Button aria-label="cancel" color="secondary" startIcon={<Cancel />} variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <SaveLoadingButton
                        aria-label="copy sets config"
                        disabled={!productKey.trim() || isSameProduct || isCopying}
                        Icon={FileCopy}
                        variant="contained"
                        onClick={handleCopy}
                    >
                        Copy
                    </SaveLoadingButton>
                </ActionButtons>
            </DialogContainer>
        </StyledDialog>
    );
};
